import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Field, Form } from 'react-final-form';
import { useConfirm } from 'material-ui-confirm';
import dayjs from 'dayjs';
import MaterialTable from '@material-table/core';

import { Button, Divider, TextField, Typography } from '@mui/material';
import { DeleteOutline as DeleteOutlineIcon } from '@mui/icons-material';
import Grid from 'material/components/Grid';

import {
  deleteVirtuousApiKey,
  fetchVirtuousApiKeys,
  postVirtuousApiKey,
} from 'store/actions/virtuous';
import { virtuousSelectors } from 'store/selectors/virtuous';
import { setSnackbarSuccess } from 'store/actions/global';

import { colors } from 'themes';
import Chip from 'common/components/Chip';

const isEven = number => number % 2 === 0;

const StyledButton = styled(Button)`
  margin-bottom: 40px;
`;

const StyledDivider = styled(Divider)`
  display: block;
  margin-bottom: 20px;
`;

const StyledField = styled(Field)`
  width: 50%;
  padding-top: 20px;
`;

const validateApiKey = value => (value ? undefined : 'API key is required');

const VirtuousIntegration = ({ organizationUuid }) => {
  const dispatch = useDispatch();
  const confirm = useConfirm();

  const { virtuousApiKeys } = useSelector(state => ({
    virtuousApiKeys: virtuousSelectors.getVirtuousApiKeys(state),
  }));

  const hasApiKey = Boolean(virtuousApiKeys?.length);

  useEffect(() => {
    dispatch(fetchVirtuousApiKeys(organizationUuid));
  }, [organizationUuid]);

  const handleSaveApiKey = async values => {
    try {
      await dispatch(postVirtuousApiKey(organizationUuid, values));
      await dispatch(fetchVirtuousApiKeys(organizationUuid));

      dispatch(setSnackbarSuccess({ message: 'Virtuous API key saved' }));

      return undefined;
    } catch (error) {
      return error;
    }
  };

  const handleRemoveApiKey = () => {
    confirm({
      confirmationText: 'Remove',
      description: 'This action will remove the Virtuous API key for your organization.',
      title: 'Remove Virtuous API key',
    })
      .then(() => dispatch(deleteVirtuousApiKey(organizationUuid)))
      .then(() => dispatch(fetchVirtuousApiKeys(organizationUuid)))
      .then(() => dispatch(setSnackbarSuccess({ message: 'Virtuous API key removed' })));
  };

  const apiKeysColumns = useMemo(
    () => [
      {
        field: 'isAuthed',
        render: rowData => (
          <Chip
            label={rowData?.isAuthed ? 'Authenticated' : 'Not authenticated'}
            size="medium"
            color={rowData?.isAuthed ? 'green' : 'error'}
          />
        ),
        title: 'Status',
      },
      {
        field: 'createdAt',
        render: rowData => dayjs(rowData?.createdAt).format('MMMM D, YYYY'),
        title: 'Added',
      },
      {
        field: 'createdBy',
        render: rowData =>
          `${rowData?.createdBy?.firstName || ''} ${rowData?.createdBy?.lastName || ''}`.trim(),
        title: 'Added by',
      },
      {
        align: 'right',
        field: '',
        render: () => (
          <Button
            aria-label="delete"
            onClick={handleRemoveApiKey}
            size="large"
            endIcon={<DeleteOutlineIcon />}
            color="grey"
          >
            Remove
          </Button>
        ),
        title: '',
      },
    ],
    []
  );

  return (
    <>
      <Typography variant="h6" gutterBottom>
        {hasApiKey ? 'Replace the API Key' : 'Add an API Key'}
      </Typography>
      <Typography variant="body1" color="grey" gutterBottom>
        {hasApiKey
          ? 'Saving a new key will replace the current Virtuous API key.'
          : 'Paste the API key generated in your Virtuous account to connect it with Muster.'}
      </Typography>
      <Form
        onSubmit={handleSaveApiKey}
        render={({ form, handleSubmit, pristine, submitting }) => (
          <form
            onSubmit={event =>
              handleSubmit(event)?.then(errors => {
                if (!errors) {
                  form.restart();
                }
              })
            }
          >
            <Grid container spacing={3}>
              <Grid size={6}>
                <StyledField name="apiKey" validate={validateApiKey}>
                  {({ input, meta }) => (
                    <TextField
                      {...input}
                      autoComplete="off"
                      error={Boolean(meta.touched && (meta.error || meta.submitError))}
                      fullWidth
                      helperText={meta.touched && (meta.error || meta.submitError)}
                      label="Virtuous API key"
                      type="password"
                    />
                  )}
                </StyledField>
              </Grid>
              <Grid size={3}>
                <StyledButton
                  color="primary"
                  disabled={submitting || pristine}
                  disableElevation
                  type="submit"
                  variant="contained"
                >
                  Save
                </StyledButton>
              </Grid>
            </Grid>
          </form>
        )}
      />
      <StyledDivider />
      <Typography variant="h6" gutterBottom>
        API Keys
      </Typography>
      <MaterialTable
        key="uuid"
        columns={apiKeysColumns}
        data={virtuousApiKeys || []}
        options={{
          actionsColumnIndex: -1,
          headerStyle: {
            color: colors.osloGray,
          },
          maxColumnSort: 0,
          paging: false,
          rowStyle: (_, rowNumber) => ({
            backgroundColor: isEven(rowNumber) ? colors.alabaster : colors.white,
            fontSize: '14px',
          }),
          search: false,
          showTitle: false,
        }}
        localization={{
          body: {
            emptyDataSourceMessage: 'No Virtuous API key has been added yet',
          },
        }}
        style={{
          boxShadow: 'none',
        }}
      />
    </>
  );
};

VirtuousIntegration.propTypes = {
  organizationUuid: PropTypes.string.isRequired,
};

export default VirtuousIntegration;
